// Appbar.js
import React from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
  useTheme,
  Toolbar,
  Typography,
  IconButton,
  Avatar,
  Divider,
  Button,
  Chip,
  Box,
  Stack,
  CircularProgress,
} from '@mui/material';
import { Menu, MenuItem, ListItemIcon, Tooltip } from '@mui/material';
import Paper from '@mui/material/Paper';
import { styled } from '@mui/material/styles';
import Link from '@mui/material/Link';
import Grid from '@mui/material/Grid';
import MuiAppBar from '@mui/material/AppBar';
import MenuIcon from '@mui/icons-material/Menu';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import NavigateNextOutlinedIcon from '@mui/icons-material/NavigateNextOutlined';
import AccountCircleOutlined from '@mui/icons-material/AccountCircleOutlined';
import Logout from '@mui/icons-material/Logout';
import { useAccount } from '../context/AccountContext';
import { useNotification } from '../context/NotificationContext';
import { PRODUCT_NAME } from '../common/AppConstants';
import * as AppUrls from '../common/AppUrls';
import { AuthContext } from '../context/AuthContext';
import { useIsMobile } from "./karyakramaTheme";
import SupportMenu from './SupportMenu';
import Copyright from './Copyright';
import logger from '../logger';

const drawerWidth = 240;

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const TopBar = ({ toggleDrawer, menuOpen }) => {
  const theme = useTheme();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const location = useLocation();
  const { account, updateUser } = useAccount();
  const { loggedIn, logout } = React.useContext(AuthContext);

  const dashboardOpen = location.pathname.startsWith(AppUrls.DASHBOARD_URL_PREFIX);
  const orgBranding = dashboardOpen && account?.organization?.logo;

  // Right Account Menu Navigation
  const [anchorEl, setAnchorEl] = React.useState(null);

  const handleMenuMouseEnter = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuMouseLeave = () => {
    setAnchorEl(null); // Close the menu
  };

  const handleMenuClose = (action) => {
    setAnchorEl(null);
    logger.log('Menu item clicked ' + action);
    if (action) {
      switch (action) {
        case 'Profile':
          navigate(AppUrls.PROFILE_URL);
          break;
        case 'Logout':
          logout();
          break;
        default:
          console.log('Invalid Menu Item');
      }
    }
  };

  const handleRoleSelect = async (orgId, role) => {
    if (orgId !== account?.user?.current_org || role !== account?.user?.current_role) {
      const payload = {
        current_org: orgId,
        current_role: role,
      };
      await updateUser(payload);
    }
    handleMenuClose('Profile');
  };

  return (
    <Box>
      <AppBar position="fixed" sx={{ boxShadow: 1 }}>
        <Toolbar
          disableGutters
          // maxWidth={false}
          sx={{
            width: '100%',
            maxWidth: `${theme.breakpoints.values.xxl}px`,
            mx: 'auto', // Centers the Toolbar horizontally
            pl: 1,  // 1 more will be handled by Logo/Dashboard menu icon
            pr: 1,
            py: 0.75,
            height: '64px',
            display: 'flex',
            justifyContent: 'space-between',
            gap: 0,
          }}
        >
          {/* Left Section */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexGrow: 1,
              gap: 0,
            }}
          >
            {/* Dashboard - Left Side Nav Menu */}
            {dashboardOpen && (
              <IconButton
                edge="start"
                color="inherit"
                aria-label="open drawer"
                onClick={toggleDrawer}
                sx={{
                  ml: 0,  // Originally it is leaning left hence 0 explicitly
                  pl: 1,
                  '&:hover': {
                    backgroundColor: '#548681',
                  }
                }}
              >
                {/* Logic: mobile + menuOpen -> MenuOpen, else Menu */}
                {isMobile && menuOpen ? <MenuOpenIcon /> : <MenuIcon />}
              </IconButton>
            )}

            {/* Organization Logo and Branding */}
            {/* ClassChronicle Logo and Branding */}
            {orgBranding ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  cursor: 'pointer',
                }}
                // onClick={() => navigate(`${account?.organization?.website}`)}
              >
                <Avatar
                  src={account?.organization?.logo}
                  alt={`${account?.organization?.name} Logo`}
                  sx={{
                    ml: 1.5,
                    width: 52,
                    height: 52,
                    // borderRadius: 2, // 👈 Rounded square (theme spacing, e.g., 2 = 16px)
                    boxShadow: '0 0 2px 1px rgba(255, 255, 255, 0.4)', // halo effect
                  }}
                  imgProps={{
                    referrerPolicy: 'no-referrer',  // Since the image url is hosted by google, can be removed if hosted on s3
                    onError: () => console.warn('Org logo image failed to load'),
                  }}
                >
                  {account?.organization?.name?.[0]?.toUpperCase() || ''}
                </Avatar>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <Typography
                    variant="h2"
                    color="white"
                    fontWeight="bold"
                    noWrap
                    sx={{
                      whiteSpace: "nowrap", // Prevent text from breaking
                      flexShrink: 0, // Prevents it from stretching
                      display: {
                        xs: "none",     // Hide on extra-small (mobile)
                        md: "block",    // Show on Tab and Desktop
                      },
                      ml: 0.5,
                    }}
                  >
                    {account?.organization?.brand_label}
                  </Typography>
                  <Typography
                    variant="h5"
                    color="white"
                    noWrap
                    sx={{
                      whiteSpace: "nowrap", // Prevent text from breaking
                      flexShrink: 0, // Prevents it from stretching
                      display: {
                        xs: "none",     // Hide on extra-small (mobile)
                        md: "block",    // Show on Tab and Desktop
                      },
                      ml: 0.5,
                    }}
                  >
                    {account?.organization?.brand_tagline}
                  </Typography>
                </Box>
              </Box>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  // cursor: 'pointer',
                }}
                // onClick={() => navigate('/')}
              >
                <Box
                  component="img"
                  src="/logo192_light.png"
                  alt="ClassChronicle Logo"
                  sx={{ width: 56, height: 44, borderRadius: '8px', ml: 0.5 }}
                />
                <Typography
                  variant="h1"
                  color="white"
                  fontWeight="bold"
                  noWrap
                  sx={{
                    whiteSpace: "nowrap", // Prevent text from breaking
                    flexShrink: 0, // Prevents it from stretching
                    display: {
                      xs: "none",     // Hide on extra-small (mobile)
                      md: "block",    // Show on Tab and Desktop
                    },
                    ml: 0.5,
                    cursor: 'pointer',
                  }}
                  onClick={() => navigate('/')}
                >
                  {PRODUCT_NAME}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Right Section */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: {xs: 1, sm: 1}
            }}
          >
            {/* Support and Docs Menu */}
            <SupportMenu />

            {/* Sign In Link */}
            {!loggedIn && (
              <IconButton
                color="inherit"
                onClick={() => navigate('/sign-in')}
                sx={{
                  px: {xs: 1, sm: 2.5},
                  // py: 0.5,
                  borderRadius: 2,
                  transition: 'all 0.2s ease-in-out',
                  // backgroundColor: isHovered ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                  '&:hover': {
                    backgroundColor: '#548681',
                    transform: 'translateY(-1px)',
                  },
                  display: {
                    xs: "none",     // Hide on extra-small (mobile)
                    md: "block",    // Show on small and larger
                  },
                }}
              >
                <Box sx={{
                  alignItems: 'center',
                }}>
                  <Typography 
                    variant="h4"
                    sx={{
                      // fontWeight: 500,
                      // fontSize: '0.875rem',
                      // lineHeight: 1
                      fontSize: {
                        xs: '0.87rem',  // e.g., smaller on mobile
                        sm: '1.1rem' // larger on tablet+
                      },
                    }}
                  >
                    Sign In
                  </Typography>
                </Box>
              </IconButton>
            )}
            
            {/* Free Trial Button */}
            {!loggedIn && (
              <Box sx={{ backgroundColor: '#0e8a0a', borderRadius: 1, }}>
                <Button
                  onClick={() => navigate('/sign-up')}
                  variant="outlined"
                  color="#ffffff"
                  sx={{
                    px: {xs: 1, sm: 2.5},
                    fontSize: {
                      xs: '0.87rem',  // e.g., smaller on mobile
                      sm: '1.0rem' // larger on tablet+
                    },
                    fontWeight: 500,
                  }}
                >
                  Free Trial
                </Button>
              </Box>
            )}

            {/* My Dashboard Button */}
            {loggedIn && !dashboardOpen && (
              <Button
                onClick={() => navigate(AppUrls.DASHBOARD_URL)}
                variant="outlined"
                color="white"
                sx={{
                  px: {xs: 1, md: 2},
                  fontSize: {
                    xs: '0.87rem',  // e.g., smaller on mobile
                    sm: '1.0rem' // larger on tablet+
                  },
                  fontWeight: 500,
                }}
              >
                Dashboard
              </Button>
            )}

            {/* Account Avatar */}
            {loggedIn && (
              <Box>
                <Tooltip title="My Account">
                  <IconButton
                    color="inherit"
                    onMouseEnter={handleMenuMouseEnter} // Open menu on hover
                    sx={{
                      pl: {xs: 1, sm: 2.5},
                      pr: {xs: 0.25, sm: 0.5}, // To Right align
                    }}
                    size="medium"
                    // sx={{ ml: 1 }}
                    // aria-controls={open ? 'account-menu' : undefined}
                    aria-haspopup="true"
                    // aria-expanded={open ? 'true' : undefined}
                  >
                    <Avatar
                      src={account?.user?.profile_picture}
                      alt={account?.user?.first_name || ''}
                      sx={{
                        width: {xs: 28, sm: 36},
                        height: {xs: 28, sm: 36},
                        // border: '2px solid white', // optional: sharp inner edge
                        boxShadow: '0 0 2px 2px rgba(255, 255, 255, 0.5)', // halo effect
                      }}
                      imgProps={{
                        referrerPolicy: 'no-referrer',  // Since the image url is hosted by google, can be removed if hosted on s3
                        onError: () => console.warn('Avatar image failed to load'),
                      }}
                    >
                      {account?.user?.first_name?.[0]?.toUpperCase() || ''}
                    </Avatar>
                  </IconButton>
                </Tooltip>
              </Box>
            )}

            {/* Account Menu */}
            <Menu
              id="account-menu"
              anchorEl={anchorEl}
              // keepMounted
              open={Boolean(anchorEl)}
              onClose={() => handleMenuClose(null)}
              anchorOrigin={{
                vertical: 'bottom', // Anchors menu below the button
                horizontal: 'right', // Aligns menu to the right
              }}
              transformOrigin={{
                vertical: 'top', // Opens from the top
                horizontal: 'right', // Stays aligned to the right
              }}
              sx={{
                mt: 2.5, // Adds margin-top (pushes menu down)
                mr: 0, // Adds margin-right (moves menu left slightly)
                '& .MuiPaper-root': {
                  backgroundColor: '#f6f8fb',
                  minWidth: 300,
                  borderRadius: 2,
                  boxShadow: 3,
                },
              }}
              MenuListProps={{
                onMouseLeave: handleMenuMouseLeave, // Close menu when mouse leaves
              }}
              disableAutoFocusItem={true} // Prevents first item auto-selection
            >
              {/* Account Menu Header */}
              <MenuItem
                disableRipple
                sx={{
                  pointerEvents: 'none',
                  cursor: 'default',
                  '&:hover': {
                    backgroundColor: 'transparent', // Prevents hover highlight
                  },
                }}
              >
                <Grid container justifyContent="center">
                  <Grid item xs={12} textAlign="center">
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
                      { !account?.user?.email && (
                        <CircularProgress size={36} thickness={2} />
                      )}
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 'normal',
                          lineHeight: 1.0,
                          color: 'text.secondary'
                          
                        }}
                      >
                        {account?.user?.email}
                      </Typography>
                      <ListItemIcon>
                        <Avatar
                          src={account?.user?.profile_picture}
                          alt={account?.user?.first_name || ''}
                          sx={{
                            mt: 1,
                            width: {xs: 56, sm: 72},
                            height: {xs: 56, sm: 72},
                            // borderRadius: 2, // 👈 Rounded square (theme spacing, e.g., 2 = 16px)
                            boxShadow: '0 0 2px 2px rgba(255, 255, 255, 0.5)', // halo effect
                          }}
                          imgProps={{
                            referrerPolicy: 'no-referrer',  // Since the image url is hosted by google, can be removed if hosted on s3
                            onError: () => console.warn('Avatar image failed to load'),
                          }}
                        >
                          {account?.user?.first_name?.[0]?.toUpperCase() || ''}
                        </Avatar>
                      </ListItemIcon>
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 'normal',
                          lineHeight: 1.2,
                          textTransform: 'capitalize'
                        }}
                      >
                        Hi, {(account?.user?.first_name || "").split(" ")[0]}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </MenuItem>

              {/* Primary Menu Items - Profile Section */}
              <Paper
                elevation={0}
                sx={{
                  mt: 1,
                  mx: 0.5, // Add some horizontal margin
                  overflow: 'hidden', // Ensure a clean look
                  // borderRadius: 4, // Rounded corners
                }}
              >
                {/* All Profiles */}
                {account?.user?.org_and_role_list?.map((org) =>
                  org.role_list?.map((role) => {
                    const isCurrent = org.id === account?.user?.current_org && role === account?.user?.current_role;
                    
                    return (
                      <MenuItem
                        key={`${org.id}-${role}`}
                        onClick={() => handleRoleSelect(org.id, role)}
                        sx={{
                          backgroundColor: isCurrent ? '#eff5fc' : '#ffffff',
                          minHeight: '48px !important',
                          padding: '8px 16px',
                          // '&:hover': {
                          //   backgroundColor: isCurrent ? '#bbdefb' : '#f5f5f5'
                          // },
                          gap: 1,
                          borderLeft: isCurrent ? '4px solid #233836' : '4px solid transparent', // optional visual cue
                          borderRadius: '4px',
                        }}
                      >
                        <ListItemIcon>
                          <Avatar
                            src={org.logo}
                            alt={role || ''}
                            sx={{
                              width: { xs: 24, sm: 32 },
                              height: { xs: 24, sm: 32 },
                              boxShadow: '0 0 2px 2px rgba(255, 255, 255, 0.5)', // halo effect
                            }}
                            imgProps={{
                              referrerPolicy: 'no-referrer',
                              onError: () => console.warn('Avatar image failed to load'),
                            }}
                          >
                            {role?.[0]?.toUpperCase() || ''}
                          </Avatar>
                        </ListItemIcon>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.25, flexGrow: 1, }}>
                          <Typography
                            variant="h5"
                            sx={{
                              fontWeight: 'normal',
                              lineHeight: 1.2,
                              textTransform: 'capitalize'
                            }}
                          >
                            {role}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 'normal',
                              lineHeight: 1.0,
                              color: 'text.secondary'
                            }}
                          >
                            {org.name}
                          </Typography>
                        </Box>
                        <NavigateNextOutlinedIcon />
                      </MenuItem>
                    )
                  })
                )}

                {/* Logout */}
                <MenuItem
                  onClick={() => handleMenuClose('Logout')}
                  sx={{
                    backgroundColor: '#ffffff',
                    minHeight: '48px !important',
                  }}
                >
                  <ListItemIcon>
                    <Logout
                      fontSize="small"
                      sx={{
                        ml: 0.5,
                        width: { xs: 18, sm: 24 },
                        height: { xs: 18, sm: 24 },
                      }}
                    />
                  </ListItemIcon>
                  Logout
                </MenuItem>
              </Paper>

              {/* Copyright */}
              <MenuItem
                disableRipple
                sx={{
                  mt: 3,
                  pointerEvents: 'none',
                  cursor: 'default',
                  '&:hover': {
                    backgroundColor: 'transparent', // Prevents hover highlight
                  },
                  px: 1.5,
                  pt: 1.5,
                }}
              >
                <Grid container justifyContent="center">
                  <Grid item xs={12} textAlign="center">
                    <Copyright sx={{ pointerEvents: 'auto' }} />
                  </Grid>
                </Grid>
              </MenuItem>

              {/* Terms and Policies */}
              <MenuItem
                disableRipple
                sx={{
                  mt: 0,
                  pointerEvents: 'none',
                  cursor: 'default',
                  '&:hover': {
                    backgroundColor: 'transparent', // Prevents hover highlight
                  },
                  px: 1.5,
                }}
              >
                <Grid container justifyContent="center">
                  <Grid item xs={6} textAlign="center">
                    <Link
                      variant="body2"
                      color="inherit"
                      href="/terms-of-service"
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        textDecoration: 'none',
                        pointerEvents: 'auto',
                      }}
                    >
                      Terms of Service
                    </Link>
                  </Grid>
                  <Grid item xs={6} textAlign="center">
                    <Link
                      variant="body2"
                      color="inherit"
                      href="/privacy-policy"
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        textDecoration: 'none',
                        pointerEvents: 'auto',
                      }}
                    >
                      Privacy Policy
                    </Link>
                  </Grid>
                </Grid>
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>
    </Box>
  );
};

export default TopBar;
